import type { ColumnDef } from "@tanstack/react-table";
import { AlertCircle, Check, Clock, ExternalLink, Loader2 } from "lucide-react";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ExternalConnectionLinks } from "@/hooks/api/use-external-connection-links";
import type { Opportunity } from "@/hooks/api/use-opportunities";
import {
  DEFAULT_COLUMN_WIDTH,
  DEFAULT_PRIMARY_COLUMN_WIDTHS,
} from "@/lib/column-defaults";
import { formatDate } from "@/lib/date-formatting";
import {
  getExternalConnectionLink,
  getExternalSystemName,
} from "@/lib/external-connection-links";
import { createCustomFieldColumns } from "@/pages/accounts/account-list/custom-field-column-utils";
import type { CustomFieldDefinition } from "@/types/custom-fields";
import type { UnifiedTableColumn } from "@/types/table-column";
import { capitalizeFirstLetter } from "@/utils/formatters";

export interface OpportunityColumnsOptions {
  filterOptions?: {
    stage_name?: string[];
    type?: string[];
    record_type_name?: string[];
  };
  connections?: ExternalConnectionLinks[];
  isLoadingConnections?: boolean;
  // Custom fields
  customFields?: CustomFieldDefinition[];
}

export const getOpportunityColumnDefs = ({
  filterOptions,
  connections,
  isLoadingConnections,
  customFields,
}: OpportunityColumnsOptions): ColumnDef<Opportunity>[] => {
  const columns = getOpportunityColumns({
    filterOptions,
    connections,
    isLoadingConnections,
    customFields,
  });

  // Convert UnifiedTableColumn to TanStack ColumnDef
  return columns
    .filter((col) => !col.exportOnly)
    .map((col) => ({
      id: col.key.toString(),
      accessorKey: col.key.toString(),
      header: col.title,
      cell: ({ row }) =>
        col.render
          ? col.render(row.original)
          : row.getValue(col.key.toString()),
      enableSorting: !!col.sortable,
      enableResizing: true,
      meta: {
        title: col.title,
        tooltip: col.tooltip, // For metric column tooltips
        categoryStyle: col.categoryStyle, // For metric column styling
        sortable: col.sortable,
        filterable: col.filterable,
        filterType: col.filterType,
        filterOptions: col.filterOptions,
        exportable: col.exportable,
        exportValue: col.exportValue,
        exportOnly: col.exportOnly,
        isCustomField: col.isCustomField,
        customFieldDataType: col.customFieldDataType,
        dataType: col.dataType,
        required: col.required,
      },
      size: col.size || DEFAULT_COLUMN_WIDTH, // Use column-specific size or default
    }));
};

export const getOpportunityColumns = ({
  filterOptions,
  connections,
  isLoadingConnections,
  customFields,
}: OpportunityColumnsOptions): UnifiedTableColumn<Opportunity>[] => {
  const columns: UnifiedTableColumn<Opportunity>[] = [
    {
      key: "name",
      title: "Opportunity Name",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      required: true, // This is the primary column that cannot be hidden
      size: DEFAULT_PRIMARY_COLUMN_WIDTHS.MEDIUM,
      render: (opportunity: Opportunity) => opportunity.name,
    },
    {
      key: "type",
      title: "Opportunity Type",
      sortable: true,
      filterable: true,
      filterType: "select",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 150, // Fixed
      filterOptions:
        filterOptions?.type?.map((type) => ({
          id: type,
          label: type,
        })) || [],
      render: (opportunity: Opportunity) => opportunity.type || "N/A",
    },
    {
      key: "record_type_name",
      title: "Record Type",
      sortable: true,
      filterable: true,
      filterType: "select",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 130, // Fixed
      filterOptions:
        filterOptions?.record_type_name?.map((name) => ({
          id: name,
          label: name,
        })) || [],
      render: (opportunity: Opportunity) =>
        opportunity.record_type_name || "N/A",
    },
    {
      key: "stage_name",
      title: "Stage",
      sortable: true,
      filterable: true,
      filterType: "select",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 120, // Fixed
      filterOptions:
        filterOptions?.stage_name?.map((stage) => ({
          id: stage,
          label: stage,
        })) || [],
      render: (opportunity: Opportunity) => opportunity.stage_name || "N/A",
    },
    {
      key: "account_name",
      title: "Account Name",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 180, // Fixed
      render: (opportunity: Opportunity) => opportunity.account_name || "N/A",
    },
    {
      key: "created_date",
      title: "Created Date",
      sortable: true,
      filterable: true,
      filterType: "date",
      defaultSortDirection: "desc",
      nullSort: "min",
      size: 120, // Fixed
      render: (opportunity: Opportunity) =>
        opportunity.created_date ? formatDate(opportunity.created_date) : "N/A",
      exportValue: (opportunity: Opportunity) =>
        formatDate(opportunity.created_date),
    },
    {
      key: "close_date",
      title: "Close Date",
      sortable: true,
      filterable: true,
      filterType: "date",
      defaultSortDirection: "desc",
      nullSort: "last",
      size: 120, // Fixed
      render: (opportunity: Opportunity) =>
        opportunity.close_date ? formatDate(opportunity.close_date) : "N/A",
      exportValue: (opportunity: Opportunity) =>
        formatDate(opportunity.close_date),
    },
    {
      key: "total_amount",
      title: "Amount",
      sortable: true,
      filterable: true,
      filterType: "number",
      defaultSortDirection: "desc",
      nullSort: "last",
      size: 120, // Fixed
      render: (opportunity: Opportunity) =>
        opportunity.total_amount?.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 0,
        }) ?? "N/A",
      exportValue: (opportunity: Opportunity) =>
        opportunity.total_amount?.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 0,
        }) ?? "",
    },
    {
      key: "buying_group_count",
      title: "Buying Group Size",
      sortable: true,
      filterable: true,
      filterType: "number",
      defaultSortDirection: "desc",
      nullSort: "last",
      size: 140, // Fixed
      render: (opportunity: Opportunity) =>
        opportunity.buying_group_count?.toString() || "0",
      exportValue: (opportunity: Opportunity) =>
        opportunity.buying_group_count?.toString() || "0",
    },
    // Touchpoint metric columns - use metric_ prefix for consistent filter handling
    {
      key: "metric_touchpoint_count",
      title: "# Touchpoints",
      tooltip: "Count of touchpoints. Respects touchpoint filters.",
      columnType: "metric",
      category: "Touchpoint",
      categoryStyle: CATEGORY_STYLES.Touchpoint?.color,
      sortable: true,
      filterable: true,
      filterType: "number",
      defaultSortDirection: "desc",
      nullSort: "min",
      size: 140, // Fixed
      render: (opportunity: Opportunity) =>
        opportunity.touchpoint_count?.toString() || "0",
      exportValue: (opportunity: Opportunity) =>
        opportunity.touchpoint_count?.toString() || "0",
    },
    {
      key: "metric_first_touchpoint_date",
      title: "First Touchpoint Date",
      tooltip: "Date of the first touchpoint. Respects touchpoint filters.",
      columnType: "metric",
      category: "Touchpoint",
      categoryStyle: CATEGORY_STYLES.Touchpoint?.color,
      sortable: true,
      filterable: true,
      filterType: "date",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 180, // Fixed
      defaultHidden: true, // Hidden by default, users can enable
      render: (opportunity: Opportunity) =>
        opportunity.first_touchpoint_date
          ? formatDate(opportunity.first_touchpoint_date)
          : "N/A",
      exportValue: (opportunity: Opportunity) =>
        formatDate(opportunity.first_touchpoint_date),
    },
    {
      key: "metric_last_touchpoint_date",
      title: "Last Touchpoint Date",
      tooltip: "Date of the last touchpoint. Respects touchpoint filters.",
      columnType: "metric",
      category: "Touchpoint",
      categoryStyle: CATEGORY_STYLES.Touchpoint?.color,
      sortable: true,
      filterable: true,
      filterType: "date",
      defaultSortDirection: "desc",
      nullSort: "last",
      size: 180, // Fixed
      defaultHidden: true, // Hidden by default, users can enable
      render: (opportunity: Opportunity) =>
        opportunity.last_touchpoint_date
          ? formatDate(opportunity.last_touchpoint_date)
          : "N/A",
      exportValue: (opportunity: Opportunity) =>
        formatDate(opportunity.last_touchpoint_date),
    },
  ];

  columns.push({
    key: "direction",
    title: "Direction",
    sortable: true,
    filterable: true,
    filterType: "select",
    defaultSortDirection: "desc",
    nullSort: "last",
    size: 140, // Fixed
    filterOptions: [
      { id: "INBOUND", label: "Inbound" },
      { id: "OUTBOUND", label: "Outbound" },
      { id: "UNDETERMINED", label: "Undetermined" },
    ],
    render: (opportunity: Opportunity) => {
      const value = opportunity.direction;
      if (!value) return null;
      return <span>{capitalizeFirstLetter(value.toLowerCase())}</span>;
    },
    exportValue: (opportunity: Opportunity) => opportunity.direction || "",
  });

  // Direction generation status column - shows the task processing status
  columns.push({
    key: "direction_generation_status",
    title: "Direction Generation Status",
    sortable: false, // Disabled as requested
    filterable: false, // Disabled as requested
    size: 180, // Fixed
    defaultHidden: true, // Hidden by default in column selector
    render: (opportunity: Opportunity) => {
      const status = opportunity.direction_generation_status;
      if (!status) return null;

      // Map status to user-friendly display
      let displayText = "";
      let icon: React.ReactNode | null = null;

      switch (status) {
        case "pending":
          displayText = "Pending";
          icon = <Clock className="h-4 w-4 text-muted-foreground" />;
          break;
        case "processing":
          displayText = "Processing";
          icon = <Loader2 className="h-4 w-4 animate-spin text-primary" />;
          break;
        case "completed":
          displayText = "Success";
          icon = <Check className="h-4 w-4 text-green-600" />;
          break;
        case "failed":
          displayText = "Failed";
          icon = <AlertCircle className="h-4 w-4 text-destructive" />;
          break;
        default:
          displayText = capitalizeFirstLetter(status);
      }

      return (
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-muted-foreground">{displayText}</span>
        </div>
      );
    },
    exportValue: (opportunity: Opportunity) =>
      opportunity.direction_generation_status || "",
  });

  // New decomposed milestone analysis column - shows the approval status
  columns.push({
    key: "milestone_analysis",
    title: "Milestone Analysis",
    sortable: true,
    filterable: true,
    filterType: "select",
    defaultSortDirection: "desc",
    nullSort: "last",
    size: 160, // Fixed
    filterOptions: [
      { id: "published", label: "Published" },
      { id: "pending_approval", label: "Pending Approval" },
    ],
    render: (opportunity: Opportunity) => {
      const value = opportunity.milestone_analysis;
      if (!value) return null;

      if (value === "published") {
        return (
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4 text-green-600" />
            <span>Published</span>
          </div>
        );
      } else if (value === "pending_approval") {
        return (
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-yellow-600" />
            <span>Pending Approval</span>
          </div>
        );
      }
      return null;
    },
    exportValue: (opportunity: Opportunity) =>
      opportunity.milestone_analysis || "",
  });

  // Milestone analysis generation status column - shows the task processing status
  columns.push({
    key: "milestone_analysis_generation_status",
    title: "Milestone Generation Status",
    sortable: false, // Disabled as requested
    filterable: false, // Disabled as requested
    size: 180, // Fixed
    defaultHidden: true, // Hidden by default in column selector
    render: (opportunity: Opportunity) => {
      const status = opportunity.milestone_analysis_generation_status;
      if (!status) return null;

      // Map status to user-friendly display
      let displayText = "";
      let icon: React.ReactNode | null = null;

      switch (status) {
        case "pending":
          displayText = "Pending";
          icon = (
            <Clock className="h-4 w-4 text-muted-foreground" />
          ) as React.ReactNode;
          break;
        case "processing":
          displayText = "Processing";
          icon = <Loader2 className="h-4 w-4 animate-spin text-primary" />;
          break;
        case "completed":
          displayText = "Success";
          icon = <Check className="h-4 w-4 text-green-600" />;
          break;
        case "failed":
          displayText = "Failed";
          icon = <AlertCircle className="h-4 w-4 text-destructive" />;
          break;
        default:
          displayText = capitalizeFirstLetter(status);
      }

      return (
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-muted-foreground">{displayText}</span>
        </div>
      );
    },
    exportValue: (opportunity: Opportunity) =>
      opportunity.milestone_analysis_generation_status || "",
  });

  // Source Opportunity ID column with external link (matches Accounts table behavior)
  columns.push({
    key: "source_record_id",
    title: "Source Record ID",
    sortable: false,
    filterable: true,
    filterType: "text",
    size: 150, // Fixed
    render: (opportunity: Opportunity) => {
      if (isLoadingConnections) {
        return <Skeleton className="h-4 w-4" />;
      }

      const url = getExternalConnectionLink(opportunity, connections);
      const systemName = getExternalSystemName(
        opportunity.source_record_type || "",
      );

      if (!url) {
        return (
          <span className="text-muted-foreground">
            {opportunity.source_record_id || "-"}
          </span>
        );
      }

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 hover:underline"
            >
              <span>{opportunity.source_record_id}</span>
              <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
            </a>
          </TooltipTrigger>
          <TooltipContent>
            <p>View in {systemName}</p>
          </TooltipContent>
        </Tooltip>
      );
    },
    exportValue: (opportunity: Opportunity) =>
      opportunity.source_record_id || "",
  });

  // Append custom field columns if provided
  if (customFields && customFields.length > 0) {
    const customFieldColumns = createCustomFieldColumns(customFields);
    return [
      ...columns,
      ...customFieldColumns,
    ] as UnifiedTableColumn<Opportunity>[];
  }

  return columns;
};
